import { Button, Flex, HStack, Link, Text, useToast } from "@chakra-ui/react"
import format from "date-fns/format"
import React, { useMemo } from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { IBeaconFragment, useDeleteIBeaconMutation, useMeQuery } from "../../graphql"
import { checkForPermissions } from "../../utils"
import { Card, Stat } from "../common"

export type IBeaconDetailsProps = {
	ibeacon: IBeaconFragment
}

export const IBeaconDetails: React.FC<IBeaconDetailsProps> = ({ ibeacon }) => {
	const [{ data: meData }] = useMeQuery()
	const [{ fetching: deleting }, deleteIBeacon] = useDeleteIBeaconMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const canUpdate = useMemo(() => checkForPermissions(meData?.me, "ibeacons/update-ibeacon")[0], [meData?.me])
	const canDelete = useMemo(() => checkForPermissions(meData?.me, "ibeacons/delete-ibeacon")[0], [meData?.me])
	const canRemoveFromZone = useMemo(() => checkForPermissions(meData?.me, "ibeacons/remove-ibeacon-from-zone")[0], [meData?.me])

	const localizedName = useMemo(() => {
		const locale = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2).toLowerCase() : undefined

		if (locale === "hi") return ibeacon.translation?.hi?.name || ibeacon.label.name
		if (locale === "es") return ibeacon.translation?.es?.name || ibeacon.label.name

		// Default to English if available, otherwise fall back to base label name
		return ibeacon.translation?.en?.name || ibeacon.label.name
	}, [ibeacon])

	const handleDelete = async () => {
		const shouldDelete = window.confirm("Are you sure you want to delete this iBeacon?")

		if (!shouldDelete) {
			return
		}

		const { data, error } = await deleteIBeacon({ ibeaconId: ibeacon._id })

		if (error) {
			toast({ description: error.message.replace("[GraphQL] ", ""), status: "error" })
			return
		}

		if (data?.deleteIBeacon) {
			toast({ description: "iBeacon deleted successfully", status: "success" })
			navigate("/ibeacons", { replace: true })
			return
		}

		toast({ description: "Couldn't delete the iBeacon", status: "error" })
	}

	return (
		<Card
			title="Details"
			alwaysShowAction
			action={
				<HStack>
					{canUpdate && (
						<Button as={NavLink} to={`/ibeacons/${ibeacon._id}/update`} colorScheme="primary" size="sm">
							Update
						</Button>
					)}
					{canRemoveFromZone && ibeacon.assignedToZoneId && (
						<Button as={NavLink} to={`/zones/${ibeacon.assignedToZoneId}/ibeacons/${ibeacon._id}/remove`} size="sm">
							Manage Zone
						</Button>
					)}
					{canDelete && (
						<Button colorScheme="error" size="sm" isLoading={deleting} onClick={handleDelete}>
							Delete
						</Button>
					)}
				</HStack>
			}
		>
			<Flex justify="center" align="center" flexWrap="wrap">
				<Stat label="Name" value={localizedName} />
				<Stat label="UUID" value={ibeacon.uuid} />
				<Stat label="Major" value={ibeacon.major} />
				<Stat label="Minor" value={ibeacon.minor} />
				<Stat label="TX Power" value={`${ibeacon.txPower} dBm`} />
				<Stat
					label="Assigned Zone"
					value={
						ibeacon.assignedToZoneId ? (
							<Link as={NavLink} to={`/zones/${ibeacon.assignedToZoneId}`}>
								View Zone
							</Link>
						) : (
							<Text color="grayscale.label">Not assigned</Text>
						)
					}
				/>
				<Stat label="Assigned At" value={ibeacon.assignedAt ? format(new Date(ibeacon.assignedAt), "MMM dd, yyyy HH:mm") : "Not available"} />
				<Stat label="Created" value={format(new Date(ibeacon.createdAt), "MMM dd, yyyy HH:mm")} />
				<Stat label="Updated" value={format(new Date(ibeacon.updatedAt), "MMM dd, yyyy HH:mm")} />
			</Flex>
		</Card>
	)
}
